import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle, Loader2, XCircle, Home, FileText } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";
const LIGHT_LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, failed
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 5;

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId || !token) {
      setStatus("failed");
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setPaymentDetails(response.data);

        if (response.data.payment_status === "paid") {
          setStatus("success");
        } else if (pollCount >= maxPolls - 1) {
          setStatus("failed");
        } else {
          setPollCount(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        }
      } catch (error) {
        if (pollCount >= maxPolls - 1) {
          setStatus("failed");
        } else {
          setPollCount(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, token, pollCount]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <Link to="/" className="mb-8">
        <img src={LOGO_URL} alt="TyreMatch" className="h-16" />
      </Link>

      <Card className="w-full max-w-md glass-card" data-testid="payment-result-card">
        {status === "loading" && (
          <>
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
              </div>
              <CardTitle className="font-heading text-2xl">Processing Payment</CardTitle>
              <CardDescription>
                Please wait while we confirm your payment...
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-sm text-muted-foreground">
                Checking status ({pollCount + 1}/{maxPolls})
              </p>
            </CardContent>
          </>
        )}

        {status === "success" && (
          <>
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <CardTitle className="font-heading text-2xl text-green-600">Payment Successful!</CardTitle>
              <CardDescription>
                Your order has been confirmed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {paymentDetails && (
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount Paid</span>
                    <span className="font-bold">
                      ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                    </span>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <h4 className="font-medium">What happens next?</h4>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    The tyre shop will receive your order details
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    They'll contact you to arrange a fitment time
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    You'll receive a confirmation email shortly
                  </li>
                </ul>
              </div>

              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => navigate("/customer/dashboard")}
                  className="w-full bg-primary text-primary-foreground"
                  data-testid="go-to-dashboard-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  View My Orders
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/")}
                  className="w-full"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </>
        )}

        {status === "failed" && (
          <>
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
                <XCircle className="h-8 w-8 text-destructive" />
              </div>
              <CardTitle className="font-heading text-2xl text-destructive">Payment Issue</CardTitle>
              <CardDescription>
                We couldn't confirm your payment
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                This could be due to a timeout or network issue. Please check your email for confirmation, or contact support if you were charged.
              </p>

              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => navigate("/customer/dashboard")}
                  className="w-full bg-primary text-primary-foreground"
                >
                  Check My Orders
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/")}
                  className="w-full"
                >
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
